// phase-parity-document-operation-staging — prior-document picker.
//
// V1 parity: when a document's lifecycle operation is set to replace / append /
// delete, the user picks the exact prior-sequence document it acts on. This
// modal lists eligible priors from the SAME backend query the compiler machinery
// exposes (GET /submissions/:id/prior-documents — strictly-lower SequenceNumber,
// same application, optional section filter). No frontend eligibility rules are
// duplicated; the list is whatever the server returns.
//
// Extracted to shared so the compiler can reuse it later (today it has only a
// drag-to-replace flow, not a picker).

import { MovableDialog } from './MovableDialog';
import { useEffect, useState, type ReactElement } from 'react';
import { v2, ApiError, parseApiErrorBody, type Document, type HistoricalDocument, type PriorDocumentReview } from '@/api/v2Client';
import type { LifecycleOperation } from '@/components/compiler/lifecycle';

/** A prior document as returned by GET /submissions/:id/prior-documents — a
 *  Documents row plus the owning sequence's number. */
export type PriorDocument = Document & { readonly SubSequenceNumber?: string };

export interface LifecyclePriorDocPickerProps {
  readonly submissionId: string;
  readonly currentDocumentId?: string;
  /** The operation being assigned — drives the title (replace/append/delete). */
  readonly operation: LifecycleOperation;
  /** CTD section of the staging document; filters the prior list server-side. */
  readonly sectionCode?: string | null;
  readonly onSelect: (prior: PriorDocument) => void;
  readonly onCancel: () => void;
}

export function LifecyclePriorDocPicker({
  submissionId,
  currentDocumentId,
  operation,
  sectionCode,
  onSelect,
  onCancel,
}: LifecyclePriorDocPickerProps): ReactElement {
  const [priors, setPriors] = useState<HistoricalDocument[] | null>(null);
  const [history, setHistory] = useState<PriorDocumentReview['history'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPriors(null);
    setError(null);
    setHistory(null);
    v2.submissions
      .getPriorDocumentReview(submissionId, sectionCode ?? undefined, currentDocumentId, operation)
      .then((result) => {
        if (!cancelled) { setPriors(result.data); setHistory(result.history); }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? (parseApiErrorBody(err)?.message ?? err.message) : 'Failed to load prior documents');
        setPriors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, sectionCode, currentDocumentId, operation]);

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="shared.priorDocPicker"
      title={<>Select the prior document to {operation}</>}
      ariaLabel={`Select the prior document to ${operation}`}
      testId="prior-doc-picker"
      onClose={onCancel}
      closeOnBackdrop
      width="min(560px, 92vw)"
      maxHeight="70vh"
      zIndex={1000}
    >
      <>
        <p style={{ margin: '0 0 16px 0', fontSize: 12, color: 'var(--text-muted)' }}>
          Documents from earlier sequences of this application
          {sectionCode ? ` in section ${sectionCode}` : ''}.
        </p>

        {error && (
          <div role="alert" style={{ color: 'var(--red, #b91c1c)', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {history?.status === 'incomplete' && <p role="status">History is incomplete. {history.reasons.join(' ')}</p>}

        {priors === null ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>
        ) : priors.length === 0 && !error ? (
          <div
            data-testid="prior-doc-empty"
            style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}
          >
            {history?.status === 'complete' ? `No prior documents found in earlier sequences${sectionCode ? ` for section ${sectionCode}` : ''}.` : 'No documents could be listed from the available history.'}
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {priors.map((p) => (
              <li key={p.Id}>
                <button
                  type="button"
                  data-testid={`prior-doc-${p.Id}`}
                  disabled={!p.Eligible}
                  onClick={() => onSelect(p)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: 6,
                    background: 'var(--surface2, #f9fafb)',
                    cursor: p.Eligible ? 'pointer' : 'not-allowed',
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{p.FileName}</span>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                    Seq {p.SubSequenceNumber ?? p.SequenceNumber ?? '—'}
                    {p.CtdSectionCode ? ` · ${p.CtdSectionCode}` : ''}
                  </span>
                  <span style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                    {p.HistoryState ?? 'unknown'} · {p.HistoryTrust ?? 'unavailable'}
                    {p.HistoryTrust === 'provisional' ? ' — earlier draft; finalization required before publishing' : ''}
                  </span>
                  {p.EligibilityReasons?.length > 0 && <span style={{ display: 'block', fontSize: 12 }}>{p.EligibilityReasons.join(' ')}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            data-testid="prior-doc-cancel"
            onClick={onCancel}
            style={{
              padding: '7px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--surface2)',
            }}
          >
            Cancel
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
